import { IncidentCard as ChartCard } from '@features/reports/components/incidents/dashboard/IncidentCard'
import {
  GoalReviewStatus,
  type GoalsDashboard,
  type Home,
  LineChartOptions
} from '@lmc/ts-common'
import { DonutChart, LineChart, theme } from '@lmc/ui-kit'
import type { DonutChartData } from '@lmc/ui-kit/src/dataDisplay/Charts/DonutChart/DonutChart'
import type { LineDataObject } from '@lmc/ui-kit/src/dataDisplay/Charts/LineChart/LineChart'
import { Text } from '@lmc/ui-kit/src/general'
import { Col, Radio, type RadioChangeEvent, Row } from 'antd'
import { getNominative } from 'common/utils'
import { Spinner } from 'components/generic'
import { GOALS } from 'components/routes/residents/goals'
import type dayjs from 'dayjs'
import Highcharts from 'highcharts'
import { NAMESPACE } from 'locale'
import { goalReportsConstants } from 'locale/goalReports'
import _, { capitalize } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import type { RangeValue } from 'types/shared'

interface GoalsChartsData {
  chartsData: GoalsDashboard | null
  dateRange: RangeValue<dayjs.Dayjs | string>
  resident?: string | null | undefined
  isLoading: boolean
  overdueGoalsLink?: boolean
  testId?: string
}

const HeaderRadioGroup = ({
  selectedValue,
  handleChange,
  options
}: {
  selectedValue: LineChartOptions
  handleChange: (e: RadioChangeEvent) => void
  options: { label: string; value: string }[]
}) => (
  <HeaderGroupStyled value={selectedValue} onChange={handleChange}>
    {options.map((option) => (
      <Radio.Button key={option.value} value={option.value}>
        {option.label}
      </Radio.Button>
    ))}
  </HeaderGroupStyled>
)

const GoalsCharts = ({
  chartsData,
  dateRange,
  resident,
  isLoading = false,
  overdueGoalsLink = true,
  testId
}: GoalsChartsData) => {
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const preferredNominative = getNominative(
    preferredPeopleNominative,
    t(goalReportsConstants.INDIVIDUALS)
  )

  const [selectedValue, setSelectedValue] = useState<LineChartOptions>(
    LineChartOptions.GoalsCompleted
  )

  const options = [
    {
      label: t(goalReportsConstants.GOALS_COMPLETED),
      description: '',
      value: LineChartOptions.GoalsCompleted
    },
    {
      label: t(goalReportsConstants.PERCENTAGE_ADDED),
      description: t(goalReportsConstants.PERCENTAGE_ADDED_DESCRIPTION),
      value: LineChartOptions.AverageProgressAdded
    },
    {
      label: t(goalReportsConstants.LOGS_LINKED),
      description: t(goalReportsConstants.LOGS_LINKED_DESCRIPTION),
      value: LineChartOptions.TotalLogsLinked
    }
  ]

  const handleChange = (e: RadioChangeEvent) => {
    setSelectedValue(e.target.value)
  }

  const getHeader = (type: 'label' | 'description') => {
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    )
    return selectedOption?.[type] || ''
  }

  const axisMapper = {
    [LineChartOptions.GoalsCompleted]: t(goalReportsConstants.GOALS_COMPLETED),
    [LineChartOptions.TotalLogsLinked]: t(goalReportsConstants.LOGS),
    [LineChartOptions.AverageProgressAdded]: t(goalReportsConstants.PERCENTAGE)
  }

  const currentSelectedFormatter = tooltipFormatters[selectedValue]
  const tooltipFormatter: () => void =
    currentSelectedFormatter(preferredNominative)

  const mappedDonutData: DonutChartData[] =
    chartsData?.goalsStatusData?.map((item) => ({
      y: item.statusCount,
      name: capitalize(t(item.name))
    })) || []

  const lineChartData: LineDataObject[] = [
    {
      name: t(goalReportsConstants[selectedValue]),
      data: chartsData?.lineChartsData?.[selectedValue] || []
    }
  ]

  return (
    <>
      <RowContainer
        gutter={24}
        data-testid={testId ? `${testId}-goals-charts` : 'goals-charts'}
      >
        <ColContainer xs={24} md={12}>
          <ChartCard
            title={t(goalReportsConstants.GOAL_STATUS)}
            subtitle={t(goalReportsConstants.GOAL_STATUS_DESCRIPTION)}
            divider
            testId={
              testId
                ? `${testId}-goals-charts-status-card`
                : 'goals-charts-status-card'
            }
          >
            {chartsData?.goalsStatusData && !isLoading ? (
              <DonutChart
                tooltipLabel="Goals"
                data={mappedDonutData}
                colors={[
                  theme.color.status.success,
                  theme.color.status.primary,
                  theme.color.category.healthRecords,
                  theme.color.status.warning,
                  theme.color.neutral.color4
                ]}
                displayLegend
                labelAlign="left"
                pieSize="105%"
                labelFormatter={labelFormatter}
                labelItemWidth={100}
                legendTitle={{
                  text: '<div style="width: 120px;text-align: right">Goals</div>',
                  style: {
                    color: 'black'
                  }
                }}
                renderSubtitle={renderSubtitle}
                displayLabels={false}
                data-testid={
                  testId ? `${testId}-goals-charts-donut` : 'goals-charts-donut'
                }
              />
            ) : (
              <DonutChartSpinnerContainer>
                <Spinner />
              </DonutChartSpinnerContainer>
            )}
          </ChartCard>
        </ColContainer>
        <ColContainer xs={24} md={12} extraMargin>
          <ChartCard
            title={t(goalReportsConstants.GOAL_STATISTICS)}
            subtitle={t(goalReportsConstants.GOAL_STATISTICS_DESCRIPTION)}
            divider
            testId={
              testId
                ? `${testId}-goals-charts-stats-card`
                : 'goals-charts-stats-card'
            }
          >
            <Stats
              data-testid={
                testId ? `${testId}-goals-charts-stats` : 'goals-charts-stats'
              }
            >
              <Results level={2}>{t(goalReportsConstants.RESULTS)}</Results>
              <TextContainer>
                <StyledText>
                  {t(goalReportsConstants.AVERAGE_PROGRESS)}
                </StyledText>
                <StyledText weight="medium">
                  {!isLoading && chartsData?.goalsStats?.averageProgressAdded}%
                </StyledText>
              </TextContainer>
              <TextContainer>
                <StyledText>
                  {t(goalReportsConstants.TOTAL_MILESTONES_COMPLETED)}
                </StyledText>
                <StyledText weight="medium">
                  {!isLoading && chartsData?.goalsStats?.completedMilestones}
                </StyledText>
              </TextContainer>
              <TextContainer>
                <StyledText>{t(goalReportsConstants.AVERAGE_LOGS)}</StyledText>
                <StyledText weight="medium">
                  {!isLoading && chartsData?.goalsStats?.averageLogCount}
                </StyledText>
              </TextContainer>
              {!resident && (
                <>
                  <TextContainer>
                    <StyledText>
                      {t(goalReportsConstants.GOAL_IN_PROGRESS, {
                        preferredTerm: preferredNominative
                      })}
                    </StyledText>
                    <StyledText weight="medium">
                      {!isLoading &&
                        `${chartsData?.goalsStats?.suWithGoalInProgress}/${chartsData?.goalsStats?.totalActiveResidents}`}
                    </StyledText>
                  </TextContainer>
                  <TextContainer>
                    <StyledText>
                      {t(goalReportsConstants.GOAL_OVERDUE, {
                        preferredTerm: preferredNominative
                      })}
                    </StyledText>
                    {overdueGoalsLink ? (
                      <Link
                        to={{
                          pathname: GOALS.MANAGEMENT
                        }}
                        state={{ reviewStatus: [GoalReviewStatus.overdue] }}
                      >
                        <StyledLink weight="medium">
                          {!isLoading &&
                            chartsData?.goalsStats?.suWithOverdueGoal}
                        </StyledLink>
                      </Link>
                    ) : (
                      <StyledText weight="medium">
                        {!isLoading &&
                          chartsData?.goalsStats?.suWithOverdueGoal}
                      </StyledText>
                    )}
                  </TextContainer>
                </>
              )}
            </Stats>
          </ChartCard>
        </ColContainer>
      </RowContainer>
      <RowContainer
        data-testid={
          testId
            ? `${testId}-goals-charts-line-container`
            : 'goals-charts-line-container'
        }
      >
        <ChartCard
          title={getHeader('label')}
          subtitle={getHeader('description')}
          headerAction={
            <HeaderRadioGroup
              selectedValue={selectedValue}
              handleChange={handleChange}
              options={options}
            />
          }
          testId={
            testId
              ? `${testId}-goals-charts-line-card`
              : 'goals-charts-line-card'
          }
        >
          {chartsData?.lineChartsData && !isLoading ? (
            <LineChart
              xAxisLabel={t(goalReportsConstants.TIME)}
              yAxisLabel={axisMapper[selectedValue]}
              startDate={(dateRange?.[0] as string) || ''}
              endDate={(dateRange?.[1] as string) || ''}
              data={_.cloneDeep(lineChartData)}
              colors={['#46A182']}
              dateFrame="month"
              tooltipFormatter={tooltipFormatter}
              data-testid={
                testId ? `${testId}-goals-charts-line` : 'goals-charts-line'
              }
            />
          ) : (
            <LineChartSpinnerContainer>
              <Spinner />
            </LineChartSpinnerContainer>
          )}
        </ChartCard>
      </RowContainer>
    </>
  )
}

const ColContainer = styled(Col)<{ extraMargin?: boolean }>`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    min-width: 555px;

    ${({ extraMargin }) => extraMargin && `margin-top: 24px; `}
  }
`

const RowContainer = styled(Row)`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const HeaderGroupStyled = styled(Radio.Group)`
  flex-direction: row;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex-direction: column;
    width: 250px;
    padding: 0 40px;
  }
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledLink = styled(Text)`
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
`

const TextContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const Results = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  align-self: end;
`

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  padding-top: 24px;
`

const DonutChartSpinnerContainer = styled.div`
  height: 250px;
`

const LineChartSpinnerContainer = styled.div`
  width: 723px;
  height: 200px;
`

function labelFormatter(this: Highcharts.Point) {
  return (
    `<div style="display: flex; justify-content: space-between; width: 100px;">` +
    `<span>${this.name}</span>` +
    `<span>${this.y}</span>` +
    `</div>`
  )
}

function renderSubtitle(total: number) {
  return {
    useHTML: true,
    text: `  <div style="text-align: center;">
	  <span style="font-size: 32px; color: #333333; font-family: 'Helvetica Neue', Helvetica, 'Segoe UI', Arial, sans-serif;">
		<b>${total}</b>
	  </span><br/>
	  <span style="font-size: 16px; color: #595959; font-family: 'Helvetica Neue', Helvetica, 'Segoe UI', Arial, sans-serif;">
		Total goals
	  </span>
	</div>`,
    verticalAlign: 'middle',
    align: 'center',
    style: {
      height: '24px'
    },
    y: -10,
    x: 80
  }
}

const tooltipFormatters: Record<
  LineChartOptions,
  (nominative?: string) => () => void
> = {
  goalsCompleted(nominative = ''): () => string {
    return function getGoalsCompletedFormatter(this: Highcharts.Point): string {
      const tooltipStyles = 'font-weight: bold; width: 100%'
      return `
		  <div style="${tooltipStyles}">
			${Highcharts.dateFormat('%B %Y', this.x as unknown as number)}
		  </div><br>
		  <div>${this.y} Goals completed in total for ${nominative}</div>
		`
    }
  },

  averageProgressAdded(nominative = ''): () => string {
    return function getPercentageAddedFormatter(
      this: Highcharts.Point
    ): string {
      const tooltipStyles = 'text-align: center; font-weight: bold;'
      return `
		  <div style="${tooltipStyles}">
			Average (%) progress added this month
		  </div><br>
		  <div>${this.y}% of progress across all ${nominative}'s active goals</div>
		`
    }
  },

  totalLogsLinked(): () => string {
    return function getLogsLinkedFormatter(this: Highcharts.Point): string {
      const tooltipStyles = 'text-align: center; font-weight: bold;'
      return `
		  <div style="${tooltipStyles}">
			Total number of logs linked to all goals
		  </div><br>
		  <div>${this.y} logs were linked this month</div>
		`
    }
  }
}

export default GoalsCharts
